import { InfoMessage } from "components/shared/InfoMessage";
import { LoadingMessage } from "components/shared/LoadingMessage";
import { SpinnerButton } from "components/shared/SpinnerButton";
import graphql from "babel-plugin-relay/macro";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { Message } from "shared/components/Message";
import { SignUpError } from "components/user/SignUpError";
import { accountMessages } from "domain/account/accountMessages";
import { signUp } from "domain/user/signUp";
import { RecaptchaVersion, SignUpError as SignUpErrorCode, signUpMutationResponse } from "generatedQueries/signUpMutation.graphql";
import { SignUpQuery } from "generatedQueries/SignUpQuery.graphql";
import { pick } from "lodash";
import { parse } from "query-string";
import React, { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRelayEnvironment } from "react-relay";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { Col, Form, Row } from "reactstrap";
import { routes } from "routes/routes";
import { PasswordEntryFields } from "routes/user/PasswordEntryFields";
import { FormLabel } from "shared/components/formInputs/FormLabel";
import { Recaptcha } from "shared/components/newFormInputs/Recaptcha";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { Query } from "shared/components/Query";
import { mapMessages } from "shared/mapMessages";
import { sharedMessages } from "shared/sharedMessages";
import { combine, isEmail, required } from "shared/validations/validations";
import { useIntl } from "react-intl";
import { getRecaptchaTokenV3, loadRecaptchaV3 } from "shared/recaptchaV3Functions";

const messages = mapMessages("routes.user.signup", {
    pageTitle: "Sign Up",
    recapchaValidationMessage: "Please complete the reCAPTCHA",
    signUpButton: "Sign Up",
    signUpComplete: "We have sent an email to {emailAddress}. Please check your email, follow the instructions to verify your email address, and then use the Sign In link above to sign in once your account has been verified. If you receive a password reset code instead, this means your email already exists and you can <pw>complete the password reset here</pw>.",
});

export interface ISignUpRequest {
    email: string;
    firstName: string | null;
    lastName: string | null;
    password: string;
    confirmPassword: string;
    recaptcha: string;
}

interface IEmailQueryString {
    email?: string;
}

export const SignUp: FC = () => {
    const { formatMessage } = useIntl();
    const environment = useRelayEnvironment();
    const location = useLocation();
    const queryString = parse(location.search) as IEmailQueryString;

    const [signUpSucceeded, setSignUpSucceeded] = useState(false);
    const [failureReason, setFailureReason] = useState<SignUpErrorCode | null | undefined>(undefined);
    const [isSaveInProgress, setIsSaveInProgress] = useState(false);
    const [recaptchaVersion, setRecaptchaVersion] = useState<RecaptchaVersion>("V3");
    const [recaptchaSiteKeyV3, setRecaptchaSiteKeyV3] = useState<string | undefined>(undefined);

    const methods = useForm<ISignUpRequest>({
        defaultValues: {
            confirmPassword: "",
            email: queryString.email || "",
            firstName: null,
            lastName: null,
            password: "",
            recaptcha: "",
        },
    });

    const email = methods.watch("email");
    const hasEmailInQueryString = !!queryString.email;

    return (
        <main>
            <Row className="justify-content-center">
            <Col xl={5} lg={6} md={7} sm={9}>
                <h1 id="signUp-title" className="h2"><Message message={messages.pageTitle} /></h1>
                {
                    signUpSucceeded
                        ? <InfoMessage
                            message={messages.signUpComplete}
                            messageValues={{
                                emailAddress: email,
                                pw: msg => <Link to={`${routes.user.forgotPassword.create({})}?email=${email}`}>{msg}</Link>,
                            }} />
                        : <Query<SignUpQuery>
                            query={graphql`
                                query SignUpQuery {
                                    configuration {
                                        collectNameOnSignUp
                                        passwordValidationRegex
                                        passwordValidationMessage
                                        licensingRecaptchaSiteKey
                                        licensingRecaptchaSiteKeyV3
                                    }
                                }`}
                            variables={{}}>
                            {({ error, props }) => {
                                if (error) {
                                    return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
                                }

                                if (!props) {
                                    return <LoadingMessage />;
                                }

                                const {
                                    collectNameOnSignUp,
                                    passwordValidationRegex,
                                    passwordValidationMessage,
                                    licensingRecaptchaSiteKey,
                                    licensingRecaptchaSiteKeyV3,
                                } = props.configuration ?? {};

                                if (recaptchaVersion === "V2") {
                                    setRecaptchaSiteKeyV3(undefined);
                                    if (!licensingRecaptchaSiteKey) {
                                        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
                                    }
                                }

                                if (recaptchaVersion === "V3") {
                                    if (!licensingRecaptchaSiteKeyV3) {
                                        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
                                    }
                                    loadRecaptchaV3(licensingRecaptchaSiteKeyV3);
                                    setRecaptchaSiteKeyV3(licensingRecaptchaSiteKeyV3);
                                }

                                return <FormProvider {...methods}>
                                    <Form
                                        aria-labelledby="signUp-title"
                                        onSubmit={methods.handleSubmit(onFormSubmit)}
                                        role="form"
                                    >
                                        <FormLabel
                                            message={accountMessages.emailAddressLabel}
                                            for="email"
                                            required={!hasEmailInQueryString}
                                        >
                                            <TextBox
                                                id="email"
                                                name="email"
                                                placeholder={formatMessage(accountMessages.emailAddressLabel)}
                                                register={methods.register}
                                                control={methods.control}
                                                type="email"
                                                options={combine<ISignUpRequest, "email">(
                                                    required(),
                                                    isEmail()
                                                )}
                                                aria-required={!hasEmailInQueryString}
                                                readOnly={hasEmailInQueryString}
                                            />
                                        </FormLabel>
                                        {
                                            collectNameOnSignUp &&
                                            <>
                                                <FormLabel
                                                    message={accountMessages.firstNameLabel}
                                                    for="firstName"
                                                    required={true}
                                                >
                                                    <TextBox
                                                        id="firstName"
                                                        name="firstName"
                                                        placeholder={formatMessage(accountMessages.firstNameLabel)}
                                                        register={methods.register}
                                                        control={methods.control}
                                                        options={required<ISignUpRequest, "firstName">()}
                                                        aria-required={true}
                                                    />
                                                </FormLabel>
                                                <FormLabel
                                                    message={accountMessages.lastNameLabel}
                                                    for="lastName"
                                                    required={true}
                                                >
                                                    <TextBox
                                                        id="lastName"
                                                        name="lastName"
                                                        placeholder={formatMessage(accountMessages.lastNameLabel)}
                                                        register={methods.register}
                                                        control={methods.control}
                                                        options={required<ISignUpRequest, "lastName">()}
                                                        aria-required={true}
                                                    />
                                                </FormLabel>
                                            </>
                                        }
                                        <PasswordEntryFields
                                            passwordField="password"
                                            passwordValidationRegex={passwordValidationRegex ?? undefined}
                                            passwordStrengthError={passwordValidationMessage ?? undefined}
                                        />
                                        {recaptchaVersion === "V2" && <Recaptcha
                                            name="recaptcha"
                                            control={methods.control}
                                            siteKey={licensingRecaptchaSiteKey}
                                            rules={required<ISignUpRequest, "recaptcha">(messages.recapchaValidationMessage)}
                                        />}
                                        {
                                            failureReason !== undefined &&
                                            <SignUpError
                                                failureReason={failureReason}
                                                passwordStrengthError={passwordValidationMessage ?? undefined}
                                            />
                                        }
                                        <SpinnerButton
                                            className="float-right"
                                            color="primary"
                                            isLoading={isSaveInProgress}
                                            message={messages.signUpButton}
                                        />
                                    </Form>
                                </FormProvider>;
                            }}
                        </Query>
                    }
                </Col>
            </Row>
        </main>
    );

    async function onFormSubmit(values: ISignUpRequest) {
        if (recaptchaVersion === "V3" && recaptchaSiteKeyV3) {
            try {
                values.recaptcha = await getRecaptchaTokenV3(recaptchaSiteKeyV3);
            }
            catch {
                setRecaptchaVersion("V2");
                return;
            }
        }
        setFailureReason(undefined);
        setIsSaveInProgress(true);
        signUp(
            environment,
            {
                recaptchaVersion,
                ...pick(values, ["email", "password", "firstName", "lastName", "recaptcha"]),
            },
            onResponseReceived,
            () => {
                setIsSaveInProgress(false);
                setFailureReason("ERROR");
            });
    }

    function onResponseReceived(response: signUpMutationResponse) {
        setIsSaveInProgress(false);

        if (response.licensingSignUp?.wasSuccessful) {
            setSignUpSucceeded(true);
        } else {
            if (recaptchaVersion === "V3" && response.licensingSignUp?.errors?.[0]?.errorCode === "RECAPTCHARESPONSENOTVALID") {
                setRecaptchaVersion("V2");
            }
            else {
                setFailureReason(response.licensingSignUp?.errors?.[0]?.errorCode ?? "ERROR");
            }
        }
    }
};
